import { SubpageHeader } from "@/components/global/SubpageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { Label } from "@/components/ui/label";
import { useEmployees } from "@/hooks/useEmployees";
import { cn } from "@/lib/utils";
import { Eye, EyeOff, Loader2, Mail, Search, User } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { verifyPointeusePassword } from "@/services/auth.service";
import { toast } from "sonner";

const BACKOFFICE_URL =
  (import.meta.env.BACKOFFICE_URL as string) || "http://localhost:5174";

export const Employes = () => {
  const { data, isLoading, isError, error } = useEmployees();
  const employees = data?.employees ?? [];

  const [search, setSearch] = useState("");
  const [revealedPins, setRevealedPins] = useState<Set<string>>(new Set());
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string | null>(
    null
  );
  const [isPinDialogOpen, setIsPinDialogOpen] = useState(false);
  const [pinPassword, setPinPassword] = useState("");
  const [pinDialogError, setPinDialogError] = useState("");
  const [isPinVerifying, setIsPinVerifying] = useState(false);
  const autoHideTimersRef = useRef<
    Record<string, ReturnType<typeof setTimeout> | undefined>
  >({});
  const [highlightEmployeeId, setHighlightEmployeeId] = useState<string | null>(
    null
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return employees;
    return employees.filter((e) => {
      const name = `${e.firstName ?? ""} ${e.lastName ?? ""}`.toLowerCase();
      const pin = (e.pin ?? "").toLowerCase();
      return name.includes(q) || pin.includes(q);
    });
  }, [employees, search]);

  // const activeCount = useMemo(
  //   () => employees.filter((e) => e.isActive !== false).length,
  //   [employees]
  // );
  // const inactiveCount = useMemo(
  //   () => employees.filter((e) => e.isActive === false).length,
  //   [employees]
  // );

  const scheduleAutoHide = (employeeId: string) => {
    const existing = autoHideTimersRef.current[employeeId];
    if (existing) clearTimeout(existing);

    autoHideTimersRef.current[employeeId] = setTimeout(() => {
      setRevealedPins((prev) => {
        if (!prev.has(employeeId)) return prev;
        const next = new Set(prev);
        next.delete(employeeId);
        return next;
      });

      autoHideTimersRef.current[employeeId] = undefined;
    }, 30_000);
  };

  const cancelAutoHide = (employeeId: string) => {
    const existing = autoHideTimersRef.current[employeeId];
    if (existing) clearTimeout(existing);
    autoHideTimersRef.current[employeeId] = undefined;
  };

  useEffect(() => {
    return () => {
      Object.values(autoHideTimersRef.current).forEach((t) => {
        if (t) clearTimeout(t);
      });
      autoHideTimersRef.current = {};
    };
  }, []);

  useEffect(() => {
    if (!highlightEmployeeId) return;
    const t = window.setTimeout(() => setHighlightEmployeeId(null), 5000);
    return () => window.clearTimeout(t);
  }, [highlightEmployeeId]);

  const openBackoffice = () => {
    if (!BACKOFFICE_URL) return;
    window.open(BACKOFFICE_URL, "_blank", "noopener,noreferrer");
  };

  const openPinDialog = (employeeId: string) => {
    setSelectedEmployeeId(employeeId);
    setPinPassword("");
    setPinDialogError("");
    setIsPinDialogOpen(true);
  };

  const handleVerifyAndRevealPin = async () => {
    if (!selectedEmployeeId) return;
    if (!pinPassword.trim()) {
      setPinDialogError("Veuillez saisir le mot de passe de la pointeuse.");
      return;
    }

    setIsPinVerifying(true);
    setPinDialogError("");
    try {
      await verifyPointeusePassword(pinPassword.trim());
      setRevealedPins((prev) => {
        const next = new Set(prev);
        next.add(selectedEmployeeId);
        return next;
      });
      scheduleAutoHide(selectedEmployeeId);
      const id = selectedEmployeeId;
      setIsPinDialogOpen(false);
      setPinPassword("");
      toast.success("PIN affiché");
      setHighlightEmployeeId(id);
    } catch (err) {
      setPinDialogError(
        err instanceof Error ? err.message : "Mot de passe incorrect"
      );
    } finally {
      setIsPinVerifying(false);
    }
  };

  const togglePinReveal = (employeeId: string) => {
    if (revealedPins.has(employeeId)) {
      cancelAutoHide(employeeId);
      setRevealedPins((prev) => {
        const next = new Set(prev);
        next.delete(employeeId);
        return next;
      });
      return;
    }
    openPinDialog(employeeId);
  };

  return (
    <div className="flex h-full min-h-0 flex-col bg-gray-50">
      <SubpageHeader pageTitle="Employés" />

      <div className="min-h-0 flex-1 overflow-y-auto px-4 pt-2 pb-[max(2.5rem,env(safe-area-inset-bottom))] space-y-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
          <Input
            placeholder="Rechercher un employé..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 bg-card border-border"
          />
        </div>

        <div className="space-y-3 pb-2">
        {isLoading ? (
          <div className="text-center py-16 text-muted-foreground">
            <Loader2 className="w-10 h-10 mx-auto mb-3 animate-spin opacity-60" />
          </div>
        ) : isError ? (
          <div className="text-center py-16">
            <p className="text-destructive text-sm">
              {error instanceof Error
                ? error.message
                : "Erreur lors du chargement des employés"}
            </p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <User className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>
              {search.trim()
                ? "Aucun résultat"
                : "Aucun employé enregistré"}
            </p>
          </div>
        ) : (
          filtered.map((emp) => {
            const revealed = revealedPins.has(emp.id);
            const isInactive = emp.isActive === false;

            return (
              <div
                key={emp.id}
                className={cn(
                  "bg-card rounded-xl border p-4 shadow-sm transition-colors duration-300 hover:bg-accent/30",
                  highlightEmployeeId === emp.id
                    ? "border-primary"
                    : "border-border"
                )}
              >
                <div className="flex items-center gap-3">
                  <div
                    className={cn(
                      "w-10 h-10 rounded-full flex items-center justify-center shrink-0",
                      isInactive ? "bg-muted" : "bg-primary/10"
                    )}
                  >
                    <User
                      className={cn(
                        "w-5 h-5",
                        isInactive
                          ? "text-muted-foreground"
                          : "text-primary"
                      )}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-medium text-foreground truncate">
                        {emp.firstName} {emp.lastName}
                      </h3>
                      {isInactive && (
                        <span className="text-xs bg-destructive/10 text-destructive px-2 py-0.5 rounded-full shrink-0">
                          Inactif
                        </span>
                      )}
                    </div>
                    <div className="flex flex-col sm:flex-row sm:items-center sm:gap-3 gap-0.5 text-sm text-muted-foreground mt-0.5">
                      <span>
                        PIN:{" "}
                        <span className="font-medium text-foreground">
                          {revealed ? emp.pin || "Non défini" : "••••"}
                        </span>
                      </span>
                      {emp.email ? (
                        <span className="flex items-center gap-1 truncate min-w-0">
                          <Mail className="w-3 h-3 shrink-0" />
                          <span className="truncate">{emp.email}</span>
                        </span>
                      ) : null}
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="shrink-0"
                    onClick={() => togglePinReveal(emp.id)}
                    aria-label={
                      revealed ? "Masquer le PIN" : "Voir le PIN"
                    }
                  >
                    {revealed ? (
                      <EyeOff className="w-5 h-5 text-muted-foreground" />
                    ) : (
                      <Eye className="w-5 h-5 text-primary" />
                    )}
                  </Button>
                </div>
              </div>
            );
          })
        )}
        </div>
      </div>

      <AlertDialog open={isPinDialogOpen} onOpenChange={setIsPinDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Voir le PIN employé</AlertDialogTitle>
            <AlertDialogDescription>
              Saisissez le mot de passe de la pointeuse pour afficher le PIN.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2">
            <Label htmlFor="pointeuse-password">Mot de passe pointeuse</Label>
            <PasswordInput
              key={String(isPinDialogOpen)}
              id="pointeuse-password"
              placeholder="••••••••"
              value={pinPassword}
              onChange={(e) => setPinPassword(e.target.value)}
              autoComplete="current-password"
            />
            {pinDialogError && (
              <p className="text-sm text-destructive">{pinDialogError}</p>
            )}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => {
                setPinPassword("");
                setPinDialogError("");
              }}
            >
              Annuler
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                void handleVerifyAndRevealPin();
              }}
              disabled={isPinVerifying}
            >
              {isPinVerifying ? "Vérification…" : "Voir PIN"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
